import { Card, CardContent } from "@/components/ui/card";
import { useSuppliersLookupQuery } from "@/modules/inventory/presentation/api/suppliers-queries";
import {
  TRANSFER_FILTER_BODY,
  TRANSFER_FILTER_CARD,
} from "@/modules/inventory/presentation/ui/transfers/transferCardLayout";
import {
  formatDateForInput,
  parseCalendarDateFromInput,
} from "@/shared/lib/date-time";
import { DatePicker } from "@/shared/ui/DatePicker";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useMemo } from "react";

const FILTER_ALL = "__all__" as const;

export type PurchaseOrderReportsSearch = {
  orderDateFrom?: string;
  orderDateTo?: string;
  supplierId?: string;
};

export function PurchaseOrderReportsFilter() {
  const nav = useNavigate({ from: "/app/reports/inventory/purchase-orders" });
  const search = useSearch({
    from: "/app/reports/inventory/purchase-orders",
  }) as PurchaseOrderReportsSearch;
  const suppliers = useSuppliersLookupQuery();

  const supplierOptions = useMemo(
    () => [
      { value: FILTER_ALL, label: "ທຸກຜູ້ສະໜອງ" },
      ...(suppliers.data ?? []).map((s) => ({
        value: s.id,
        label: s.code ? `${s.name} (${s.code})` : s.name,
      })),
    ],
    [suppliers.data],
  );

  const hasFilter =
    !!search.orderDateFrom || !!search.orderDateTo || !!search.supplierId;

  const update = (next: Partial<PurchaseOrderReportsSearch>) => {
    nav({ search: { ...search, ...next } });
  };

  return (
    <Card className={TRANSFER_FILTER_CARD}>
      <CardContent className={TRANSFER_FILTER_BODY}>
        <DatePicker
          mode="range"
          value={{
            from: search.orderDateFrom
              ? parseCalendarDateFromInput(search.orderDateFrom)
              : undefined,
            to: search.orderDateTo
              ? parseCalendarDateFromInput(search.orderDateTo)
              : undefined,
          }}
          onChange={(range) => {
            const from = range?.from;
            const to = range?.to;
            update({
              orderDateFrom: from ? formatDateForInput(from) : undefined,
              orderDateTo: to ? formatDateForInput(to) : undefined,
            });
          }}
          placeholder="ວັນທີສັ່ງ PO"
          className="h-9 w-fit min-w-56"
        />
        <SimpleSelect
          placeholder="ຜູ້ສະໜອງ"
          value={search.supplierId ?? FILTER_ALL}
          onValueChange={(v) => {
            const id = v === FILTER_ALL || !v ? undefined : v;
            update({ supplierId: id });
          }}
          options={supplierOptions}
          className="h-9 min-w-[12rem]"
        />
        {hasFilter ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
            onClick={() => nav({ search: {} })}
          >
            <XIcon className="size-4" />
            ລ້າງຕົວກອງ
          </button>
        ) : null}
      </CardContent>
    </Card>
  );
}
